import { Col, Container, Row } from "reactstrap";
import { MAP_DISPLAY_MODES, MAP_MODES, isResultsTab } from "../constants";
import {
  selectFilteredFeatures,
  selectSelectedTrialContractInfo,
} from "../selectors/resultMap";
import { useCallback, useMemo, useState } from "react";

import ExperimentTools from "./ExperimentTools/ExperimentTools";
import { MapLayerControl } from "./MapLayerControl/MapLayerControl";
import { NorthIndicator } from "./NorthIndicator";
import { OffCanvasWrapper } from "../analyticsDashboard/components/advancedFiltering/OffCanvasWrapper";
import { PlotView } from "./PlotView/PlotView";
import PropTypes from "prop-types";
import { RotationDial } from "./RotationDial";
import StepIndicator from "./StepIndicator/StepIndicator";
import { TileLayerControl } from "./TileLayerControl/TileLayerControl";
import { Toolbox } from "./Toolbox/Toolbox";
import { TrialList } from "./TrialList/TrialList";
import { WorldMapLegend } from "./WorldMapLegend/WorldMapLegend";
import { hasExperimentsRole } from "../users/rolesUtil";
import { useKeyboardShortcut } from "../hooks/useKeyboardShortcut";
import { useSelector } from "react-redux";

export const WorldMapHud = ({
  mapRef,
  selectedTileLayer,
  setSelectedTileLayer,
  isOffcanvasOpen,
  toggleOffcanvas,
}) => {
  const contractInfo = useSelector(selectSelectedTrialContractInfo);

  const [trial, selectedPlotId, mapMode, mapDisplayMode] = useSelector(
    ({ resultMap }) => [
      resultMap.trial,
      resultMap.selectedPlotId,
      resultMap.mapMode,
      resultMap.mapDisplayMode,
    ]
  );
  const features = useSelector(selectFilteredFeatures);

  const selectedFeature = useMemo(
    () => features.find((f) => f.id === selectedPlotId),
    [selectedPlotId, features]
  );

  const user = useSelector(({ user }) => user);

  const isFieldMapMode = mapDisplayMode === MAP_DISPLAY_MODES.FIELD_MAP;

  // "closing" keeps the dial mounted until its close animation ends
  const [dialState, setDialState] = useState("closed");
  const toggleDial = () =>
    setDialState((state) => (state === "closed" ? "open" : "closing"));
  // Stable, so the dial does not re-attach its outside-click listener on every render
  const closeDial = useCallback(() => setDialState("closing"), []);

  useKeyboardShortcut("n", toggleDial);

  return (
    <>
      <Container fluid className="page-content worldmap-hud w-100 gap-2 pb-0">
        <Row
          className="hud-top-row align-items-start g-0 pe-2"
          style={{ zIndex: 2000 }}
        >
          <Col />
          {mapMode === MAP_MODES.TRIAL && !isResultsTab(mapDisplayMode) && (
            <Col sm="5">
              <div className="powerdash-component experiment-tools d-flex flex-column gap-1 pb-2">
                <div className="d-flex flex-row gap-2 align-items-center fw-medium">
                  <i className="fa-solid fa-bars-staggered" />
                  Site preparation
                </div>
                <StepIndicator
                  plotmapStatus={trial.plotmap_status}
                  experimentStatus={trial.experiment_status}
                  siteId={trial.id}
                  user={user}
                />
                {mapRef.current &&
                  trial &&
                  mapMode === MAP_MODES.TRIAL &&
                  mapDisplayMode === MAP_DISPLAY_MODES.FIELD_MAP &&
                  hasExperimentsRole(contractInfo?.roles) && (
                    <ExperimentTools map={mapRef.current} />
                  )}
              </div>
            </Col>
          )}
        </Row>
        {/* Row always at bottom. hud-bottom-row: allowed to shrink below its
            content, or the tallest column (the layer dock, once open) makes the
            whole row overflow the sheet and drags the bottom-aligned right
            column down with it. */}
        <Row className="hud-bottom-row g-0 flex-grow-1">
          <Col className="d-flex flex-column flex-grow-1" sm="6">
            {/* hud-plot-row: allowed to shrink below its content, so the layer
                dock underneath always keeps its own height */}
            <Row className="hud-plot-row flex-grow-1 g-0 w-75">
              {selectedFeature && <PlotView feature={selectedFeature} />}
            </Row>
            <div className="hud-bottom-left g-0 pt-3 d-flex flex-row align-items-end gap-2">
              {trial && <MapLayerControl />}
            </div>
          </Col>
          <Col className="flex-grow-1 align-content-end pe-2" sm="4">
            <div className="d-flex flex-row align-items-end justify-content-end gap-2">
              {dialState === "closed" ? (
                <NorthIndicator map={mapRef.current} onClick={toggleDial} />
              ) : (
                <RotationDial
                  map={mapRef.current}
                  closing={dialState === "closing"}
                  onClose={closeDial}
                  onClosed={() => setDialState("closed")}
                />
              )}
              {trial && <Toolbox mapRef={mapRef} />}
            </div>
          </Col>
          <Col
            className="flex-grow-1 justify-content-end d-flex flex-column pb-1"
            sm="2"
          >
            {mapMode === MAP_MODES.CLUSTER && <TrialList />}
            {mapMode === MAP_MODES.TRIAL && <WorldMapLegend />}
            <TileLayerControl
              selectedTileLayer={selectedTileLayer}
              setSelectedTileLayer={setSelectedTileLayer}
            />
          </Col>
        </Row>
        {!isFieldMapMode && (
          <OffCanvasWrapper
            isOffcanvasOpen={isOffcanvasOpen}
            toggleOffcanvas={toggleOffcanvas}
          />
        )}
      </Container>
    </>
  );
};

WorldMapHud.propTypes = {
  mapRef: PropTypes.object.isRequired,
  selectedTileLayer: PropTypes.object.isRequired,
  setSelectedTileLayer: PropTypes.func.isRequired,
  isOffcanvasOpen: PropTypes.bool.isRequired,
  toggleOffcanvas: PropTypes.func.isRequired,
};
